/**
 * App Component
 *
 * Main application component with routing and OKTA authentication.
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { ROUTES, USER_ROLES } from './config/constants';
import api from './services/api';
import { oktaConfig } from './config/okta';

// Layout
import MainLayout from './components/Layout/MainLayout';

// Pages
import Dashboard from './pages/Dashboard';
import IntakeFlow from './pages/IntakeFlow';
import RequestDetail from './pages/RequestDetail';
import Reporting from './pages/Reporting';
import Admin from './pages/Admin';
import ProductForm from './pages/ProductForm';
import ProductDetail from './pages/ProductDetail';
import ProductHierarchy from './pages/ProductHierarchy';
import Help from './pages/Help';
import Login from './pages/Login';

// Styles
import './App.css';

const oktaAuth = new OktaAuth(oktaConfig);

/**
 * Route that redirects to dashboard if the user is not admin or developer.
 */
const PrivilegedRoute = ({ children, ...rest }) => {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCurrentUser()
      .then(res => setRole(res.data.data.user.role))
      .catch(() => setRole('user'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const isPrivileged = role === USER_ROLES.ADMIN || role === USER_ROLES.DEVELOPER;
  return (
    <Route {...rest}>
      {isPrivileged ? children : <Redirect to={ROUTES.DASHBOARD} />}
    </Route>
  );
};

const AppRoutes = () => {
  const history = useHistory();

  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Switch>
        <Route path={ROUTES.LOGIN}><Login /></Route>
        <Route path="/callback"><LoginCallback /></Route>

        <Route exact path={ROUTES.DASHBOARD}>
          <MainLayout>
            <Dashboard />
          </MainLayout>
        </Route>

        <Route exact path={ROUTES.INTAKE_NEW}>
          <MainLayout>
            <IntakeFlow />
          </MainLayout>
        </Route>

        <Route exact path="/intake/:id">
          <MainLayout>
            <IntakeFlow />
          </MainLayout>
        </Route>

        <Route exact path="/request/:id">
          <MainLayout>
            <RequestDetail />
          </MainLayout>
        </Route>

        <Route exact path={ROUTES.REPORTS}>
          <MainLayout>
            <Reporting />
          </MainLayout>
        </Route>

        <PrivilegedRoute exact path={ROUTES.ADMIN}>
          <MainLayout>
            <Admin />
          </MainLayout>
        </PrivilegedRoute>

        <Route exact path={ROUTES.PRODUCTS_NEW}>
          <MainLayout>
            <ProductForm />
          </MainLayout>
        </Route>

        <Route exact path={ROUTES.PRODUCTS_EDIT}>
          <MainLayout>
            <ProductForm />
          </MainLayout>
        </Route>

        <Route exact path={ROUTES.PRODUCTS_VIEW}>
          <MainLayout>
            <ProductDetail />
          </MainLayout>
        </Route>

        <PrivilegedRoute exact path={ROUTES.PRODUCT_HIERARCHY}>
          <MainLayout>
            <ProductHierarchy />
          </MainLayout>
        </PrivilegedRoute>

        <Route exact path={ROUTES.HELP}>
          <MainLayout>
            <Help />
          </MainLayout>
        </Route>

        {/* Default redirect to dashboard */}
        <Route exact path={ROUTES.HOME}>
          <Redirect to={ROUTES.DASHBOARD} />
        </Route>

        {/* 404 */}
        <Route>
          <div className="app-not-found">
            <h1>404 - Page Not Found</h1>
            <p>The page you&apos;re looking for doesn&apos;t exist.</p>
            <a href={ROUTES.DASHBOARD}>Go to Dashboard</a>
          </div>
        </Route>
      </Switch>
    </Security>
  );
};

const App = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default App;
